import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import "./TableBooking.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import Navbar from "../../../layouts/navbar";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTable } from "./TableContext";
import { useReferral } from "../../../utils/referral";
import { useCheckout } from "../../../layouts/checkout/CheckoutContext";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

function totalDaysBetweenTwoDates(startDate: any, endDate: any) {
  let start = moment(startDate, "YYYY-MM-DD");
  let end = moment(endDate, "YYYY-MM-DD");

  return moment.duration(end.diff(start)).asDays();
}

const TableCheckout = (props: any) => {
  const { navigateWithReferral } = useReferral();
  const { currentBooking, createTableBooking } = useTable();
  const { user } = useAuth();
  const { checkPromotionEligibility } = useCheckout();

  // Add referral state
  const { referralCode: urlReferralCode, clearReferral } = useReferral();
  const [manualReferral, setManualReferral] = useState("");

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");

  const [filled, setFilled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [priceType, setPriceType] = useState(0);
  const [specialRequest, setSpecialRequest] = useState("");
  const [checkedAddons, setCheckedAddons] = useState<any>([]);
  const [checkedAddonDetails, setCheckedAddonDetails] = useState([]);
  const [checkAllAddonsChecked, setCheckAllAddonsChecked] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Determine the final referral code to use
  const finalReferralCode = urlReferralCode || manualReferral;

  // Calculate table booking price based on DEPOSIT
  const calculatePrice = () => {
    const depositAmount: any =
      currentBooking.bookingParams?.timereserve?.Deposit || 0;

    return {
      deposit: parseFloat(depositAmount),
      total: parseFloat(depositAmount), // For table booking, total = deposit
    };
  };

  const prices = calculatePrice();

  const calculateFinalPrice = () => {
    const basePrices = calculatePrice();
    let discountAmount = 0;

    // Calculate discount from promo code
    if (appliedPromo && appliedPromo.eligible === "YES") {
      discountAmount = parseFloat(appliedPromo.discount_amount || "0");
    }

    return {
      baseTotal: basePrices.total,
      discountAmount: discountAmount,
      finalTotal: Math.max(0, basePrices.total - discountAmount),
    };
  };

  const finalPrices = calculateFinalPrice();

  // Promo code functions
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setApplyingPromo(true);
    setPromoError("");

    try {
      const result = await checkPromotionEligibility({
        ordertype: "table",
        promotion_code: promoCode.trim(),
        amount: finalPrices.baseTotal,
      });

      // Check if promo is eligible based on API response
      if (result.eligible === "YES") {
        setAppliedPromo(result);
        message.success(`Promo code applied! ${result.description}`);
      } else {
        setPromoError(result.description || "This promo code is not eligible");
      }
    } catch (err: any) {
      setPromoError(err.message || "Failed to apply promo code");
      message.error("Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    message.info("Promo code removed");
  };

  // Addon Management
  const onChangeAddonInput = (addonCode: any, type: any, value: any) => {
    const newDetails: any = [...checkedAddonDetails];
    const index = newDetails.findIndex(
      (item: any) => item.addon_code === addonCode
    );

    if (index !== -1) {
      newDetails[index][type] = value;
    } else {
      newDetails.push({ addon_code: addonCode, [type]: value });
    }

    setCheckedAddonDetails(newDetails);
  };

  const onCheckSingle = (checkedValues: any, allOptionValues: any) => {
    setCheckedAddons(checkedValues);

    // Build details for backend mapping
    const details = checkedValues.map((val: string) => {
      const [addonCode, idx] = val.split("_");
      const existing = checkedAddonDetails.find((d: any) => d._uid === val);
      return existing || { _uid: val, addon_code: addonCode, night: 1, pax: 1 };
    });

    setCheckedAddonDetails(details);

    // Update "Check All" status
    setCheckAllAddonsChecked(checkedValues.length === allOptionValues.length);
  };

  const onCheckAll = (checked: boolean, allAddons: any[]) => {
    if (checked) {
      const allValues = allAddons.map((addon, idx) => `${addon.value}_${idx}`);

      const details: any = allAddons.map((addon, idx) => ({
        _uid: `${addon.value}_${idx}`,
        addon_code: addon.value,
        night: 1,
        pax: 1,
      }));

      setCheckedAddons(allValues);
      setCheckedAddonDetails(details);
      setCheckAllAddonsChecked(true);
    } else {
      setCheckedAddons([]);
      setCheckedAddonDetails([]);
      setCheckAllAddonsChecked(false);
    }
  };

  const onAutofillForm = (checked: any) => {
    setFilled(checked);
    if (checked) {
      setName(`${user.firstname || ""} ${user.lastname || ""}`.trim());
      setEmail(user.email || "");
      setPhone(user.phone || "");
    } else {
      setName("");
      setEmail("");
      setPhone("");
    }
  };

  const onChangeInput = (type: any, value: any) => {
    const setters: any = {
      name: setName,
      email: setEmail,
      phone: setPhone,
      specialRequest: setSpecialRequest,
    };

    if (setters[type]) {
      setters[type](value);
    }
  };

  const onComplete = () => {
    // const params;
    // if (name === "" && email === "" && phone === "") {
    //   alert("Please fill up the required fields before proceed");
    // } else {
    //   props.onChangeStep(3);
    // }
  };

  const findAddonIndex = (addonCode: any) => {
    return checkedAddonDetails.findIndex(
      (item: any) => item.addon_code === addonCode
    );
  };

  const getAddonValue = (addonCode: any, type: any) => {
    const index = findAddonIndex(addonCode);
    if (index !== -1) {
      return checkedAddonDetails[index][type] || 1;
    }
    return 1;
  };

  const finalCheckedAddonDetailsForBackend = (checkedValues: any) => {
    return checkedValues.map((addon: any) => {
      return {
        addon_code: addon?.addon_code.split("_")[0],
        night: addon?.night,
        pax: addon?.pax,
      };
    });
  };

  const onPayNow = async () => {
    setPayingNow(true);
    const params = {
      store_id: currentBooking.bookingParams?.store_id,
      datereserve: currentBooking.bookingParams?.datereserve,
      timereserve: currentBooking.bookingParams?.timereserve.time_slot,
      number_adult: currentBooking.bookingParams?.number_adult,
      number_child: currentBooking.bookingParams?.number_child,
      name: name,
      email: email,
      phone: phone,
      promotion_code: appliedPromo?.code || "", // Add promo code
      referral: finalReferralCode || "", // Add referral code
    };

    console.log("check params ===> ", params);
    try {
      const res = await createTableBooking(params);
      if (res && res.payment_redirect_url) {
        setShowPaymentModal(true);

        // Give user time to read the message
        setTimeout(() => {
          window.location.replace(`${res.payment_redirect_url}`);
        }, 2500);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setShowPaymentModal(false);
    } finally {
      setPayingNow(false);
    }
  };

  return (
    <div id="HomeScreen">
      {/* @ts-ignore */}
      <Modal
        title="Processing Your Payment"
        visible={showPaymentModal}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <Title level={4}>Secure Payment</Title>
          <Text>You're being redirected to our secure payment gateway.</Text>
          <br />
          <br />
          <Text type="secondary">
            • Please complete your payment in the new window
            <br />
            • Do not close this page until payment is complete
            <br />• You'll be redirected back here after payment
          </Text>
          <br />
          <br />
          <div style={{ marginTop: "16px" }}>
            <Spin size="large" />
          </div>
        </div>
      </Modal>
      <div className="container">
        <Navbar hideBanner={true} />
        <div>
          <Text>
            Please fill in the details below so that we can email or call you if
            we have any enquiries regarding your information or booking. Thank
            you!
          </Text>
        </div>
        <Alert
          message={
            <div>
              <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                Your Table Booking Details
              </div>

              <div>
                <b>{currentBooking.selectedProduct?.name}</b>
              </div>

              <div>
                Date: <b>{currentBooking.bookingParams?.datereserve}</b> at
                <b> {currentBooking.bookingParams?.timereserve.time_slot}</b>
              </div>

              <div>
                <b>{currentBooking.bookingParams?.number_adult} Adults</b>
                {(currentBooking.bookingParams?.number_child || 0) > 0 && (
                  <>
                    {" & "}
                    <b>{currentBooking.bookingParams?.number_child} Children</b>
                  </>
                )}
              </div>

              {(currentBooking.bookingParams?.name ||
                currentBooking.bookingParams?.phone ||
                currentBooking.bookingParams?.email) && (
                <div>
                  Contact:{" "}
                  <b>
                    {currentBooking.bookingParams?.name ||
                      currentBooking.bookingParams?.phone ||
                      currentBooking.bookingParams?.email}
                  </b>
                </div>
              )}

              <Divider style={{ margin: "10px 0" }} />
              <div>
                <Text strong>Deposit: RM {prices.deposit.toFixed(2)}</Text>
              </div>
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                *This is a deposit amount to secure your table booking
              </div>
            </div>
          }
          type="success"
          style={{ marginTop: "1em", marginBottom: "1em" }}
        />
        <Checkbox
          checked={filled}
          style={{ marginBottom: 20 }}
          onChange={(e) => {
            onAutofillForm(e.target.checked);
          }}
        >
          Fill with my profile's info
        </Checkbox>

        <Form layout="vertical">
          <Form.Item label="Full Name" required>
            <Input
              value={name}
              onChange={(e) => {
                onChangeInput("name", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="John Doe"
            />
          </Form.Item>
          <Form.Item label="Email" required>
            <Input
              value={email}
              onChange={(e) => {
                onChangeInput("email", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="johndoe@mail.com"
            />
          </Form.Item>
          <Form.Item label="Phone No" required>
            <Input
              value={phone}
              onChange={(e) => {
                onChangeInput("phone", e.target.value);
              }}
              addonBefore={"+6"}
              placeholder="0123456789"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Referral Code Section */}
          <Form.Item label="Referral Code (optional)">
            {urlReferralCode ? (
              // If referral is bound in URL, show disabled input
              <div>
                <Input
                  value={urlReferralCode}
                  disabled
                  style={{ width: "100%" }}
                  placeholder="Referral code from URL"
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Referral code applied from your invitation link
                </Text>
              </div>
            ) : (
              // If no URL referral, allow manual input
              <Input
                value={manualReferral}
                onChange={(e) => setManualReferral(e.target.value)}
                style={{ width: "100%" }}
                placeholder="Enter referral code (optional)"
              />
            )}
          </Form.Item>

          {/* Promo Code Section */}
          <Form.Item label="Promo Code (optional)">
            <div
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
                disabled={applyingPromo || !!appliedPromo}
                style={{ flex: 1 }}
              />
              {appliedPromo ? (
                <Button onClick={handleRemovePromo} danger type="default">
                  Remove
                </Button>
              ) : (
                <Button
                  onClick={handleApplyPromo}
                  loading={applyingPromo}
                  type="primary"
                  disabled={!promoCode.trim()}
                >
                  Apply
                </Button>
              )}
            </div>
            {promoError && (
              <Text
                type="danger"
                style={{ fontSize: "12px", marginTop: "4px", display: "block" }}
              >
                {promoError}
              </Text>
            )}
            {appliedPromo && appliedPromo.eligible === "YES" && (
              <div style={{ marginTop: "4px" }}>
                <Text
                  type="success"
                  style={{ fontSize: "12px", display: "block" }}
                >
                  ✅ {appliedPromo.description}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: "11px", display: "block" }}
                >
                  Valid until:{" "}
                  {moment(appliedPromo.expirydate).format("DD MMM YYYY")}
                </Text>
              </div>
            )}
          </Form.Item>

          {/* <Form.Item
            label="Special Request (optional)"
            tooltip={{
              title:
                "e.g, Request for extra beds / Muslim prayer mat / extra mineral water / etc...",
              // icon: <InfoCircleOutlined  />,
            }}
          >
            <TextArea
              value={specialRequest}
              onChange={(e) => onChangeInput("specialRequest", e.target.value)}
              showCount
              maxLength={150}
              style={{ width: "100%" }}
            />
          </Form.Item> */}
        </Form>

        {/* Payment Summary */}
        <Card
          title="Payment Summary"
          style={{ marginBottom: 20, backgroundColor: "#f9f9f9" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <Text>Deposit</Text>
            <Text>RM {finalPrices.baseTotal.toFixed(2)}</Text>
          </div>

          {appliedPromo && appliedPromo.eligible === "YES" && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
                color: "#52c41a",
              }}
            >
              <Text>Discount ({appliedPromo.code}):</Text>
              <Text>- RM {finalPrices.discountAmount.toFixed(2)}</Text>
            </div>
          )}

          <Divider style={{ margin: "12px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: "16px" }}>
              Total Amount
            </Text>
            <Text strong style={{ fontSize: "18px", color: "#e90056" }}>
              RM {finalPrices.finalTotal.toFixed(2)}
            </Text>
          </div>

          <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
            *This deposit will be applied towards your final bill
          </div>
        </Card>

        <Checkbox
          onChange={(e) => {
            setAgreed(e.target.checked);
          }}
          checked={agreed}
          style={{ marginRight: 10 }}
        >
          {" "}
          I agree with the <a href="#">terms and conditions</a> of this company
        </Checkbox>
        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            style={{ marginRight: 10 }}
            onClick={() => {
              navigateWithReferral(-1);
            }}
          >
            Back
          </Button>
          {/* <Button>Add To Cart</Button> */}
          <Button
            type="primary"
            onClick={() => {
              onPayNow();
            }}
            loading={payingNow}
            disabled={!agreed}
            title={
              !agreed ? "Please agree to terms and conditions to proceed" : ""
            }
          >
            Pay Deposit Now
          </Button>
        </div>
        <Divider />
      </div>
    </div>
  );
};

export default TableCheckout;
